// PlaceSection Component - Horizontally scrolling place cards with emotional titles
// Used for curated sections like "Gerade beliebt", "Fuer einen ruhigen Moment", etc.

import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { Place } from '../../types';
import { PlaceCard } from '../places/PlaceCard';

interface PlaceSectionProps {
  title: string;
  subtitle?: string;
  emoji?: string;
  icon?: React.ReactNode;
  places: Place[];
  favorites: string[];
  onSelectPlace: (place: Place) => void;
  onToggleFavorite: (placeId: string) => void;
  showMoreLink?: {
    label: string;
    onClick: () => void;
  };
}

export function PlaceSection({
  title,
  subtitle,
  emoji,
  icon,
  places,
  favorites,
  onSelectPlace,
  onToggleFavorite,
  showMoreLink,
}: PlaceSectionProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Check scroll position
  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    const ref = scrollRef.current;
    if (ref) {
      ref.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
    }
    return () => {
      if (ref) {
        ref.removeEventListener('scroll', checkScroll);
      }
      window.removeEventListener('resize', checkScroll);
    };
  }, [places]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const cardWidth = 320; // Approximate card width + gap
      const scrollAmount = cardWidth * 2;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  if (places.length === 0) return null;

  return (
    <section className="py-8">
      {/* Section Header */}
      <div className="flex items-end justify-between mb-5">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            {icon && <span className="flex-shrink-0">{icon}</span>}
            {emoji && !icon && <span className="text-2xl">{emoji}</span>}
            {title}
          </h2>
          {subtitle && (
            <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
        {showMoreLink && (
          <button
            onClick={showMoreLink.onClick}
            className="text-sm font-medium text-amber-700 hover:text-amber-900
                       hover:underline transition-colors whitespace-nowrap"
          >
            {showMoreLink.label}
          </button>
        )}
      </div>

      {/* Scrollable Cards Container */}
      <div className="relative -mx-4 sm:-mx-6 lg:-mx-8">
        {/* Left Scroll Button */}
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10
                       w-10 h-10 flex items-center justify-center
                       bg-white border border-gray-200 rounded-full shadow-lg
                       hover:shadow-xl hover:scale-105 transition-all duration-200"
            aria-label="Nach links scrollen"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
        )}

        {/* Cards */}
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto no-scrollbar px-4 sm:px-6 lg:px-8 pb-2"
          style={{ scrollSnapType: 'x mandatory' }}
        >
          {places.map((place) => (
            <div
              key={place.id}
              className="flex-shrink-0 w-[280px] sm:w-[300px] lg:w-[320px]"
              style={{ scrollSnapAlign: 'start' }}
            >
              <PlaceCard
                place={place}
                isFavorite={favorites.includes(place.id)}
                onSelect={onSelectPlace}
                onToggleFavorite={onToggleFavorite}
              />
            </div>
          ))}
        </div>

        {/* Right Scroll Button */}
        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10
                       w-10 h-10 flex items-center justify-center
                       bg-white border border-gray-200 rounded-full shadow-lg
                       hover:shadow-xl hover:scale-105 transition-all duration-200"
            aria-label="Nach rechts scrollen"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        )}
      </div>
    </section>
  );
}

export default PlaceSection;
